import React from "react";
import { stripLeadingWorkflowStepNumber } from "../../utils/stripLeadingWorkflowStepNumber";
import { Box, Typography, IconButton, Chip } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import VpnKeyIcon from "@mui/icons-material/VpnKey";
import ContactPhoneIcon from "@mui/icons-material/ContactPhone";
import BusinessIcon from "@mui/icons-material/Business";
import PersonIcon from "@mui/icons-material/Person";
import AssignmentIcon from "@mui/icons-material/Assignment";
import GroupIcon from "@mui/icons-material/Group";
import FolderSharedIcon from "@mui/icons-material/FolderShared";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import DrawIcon from "@mui/icons-material/Draw";
import LockIcon from "@mui/icons-material/Lock";
import SupervisorAccountIcon from "@mui/icons-material/SupervisorAccount";
import GroupsIcon from "@mui/icons-material/Groups";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import BlockIcon from "@mui/icons-material/Block";
import LogoutIcon from "@mui/icons-material/Logout";
import SecurityIcon from "@mui/icons-material/Security";
import ForwardToInboxIcon from "@mui/icons-material/ForwardToInbox";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import ListAltIcon from "@mui/icons-material/ListAlt";
import GroupRemoveIcon from "@mui/icons-material/GroupRemove";
import MoneyOffCsredIcon from "@mui/icons-material/MoneyOffCsred";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import DescriptionIcon from "@mui/icons-material/Description";
import MoveToInboxIcon from "@mui/icons-material/MoveToInbox";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";

const STEP_ICONS: Record<string, React.ReactNode> = {
  create_user: <PersonAddIcon sx={{ fontSize: 24, color: "#4CAF50" }} />,
  set_password: <VpnKeyIcon sx={{ fontSize: 24, color: "#FF9800" }} />,
  set_contact_info: <ContactPhoneIcon sx={{ fontSize: 24, color: "#2196F3" }} />,
  set_org_info: <BusinessIcon sx={{ fontSize: 24, color: "#9C27B0" }} />,
  set_personal_info: <PersonIcon sx={{ fontSize: 24, color: "#673AB7" }} />,
  assign_license: <AssignmentIcon sx={{ fontSize: 24, color: "#FFC107" }} />,
  set_email_signature: <DrawIcon sx={{ fontSize: 24, color: "#0078D4" }} />,
  assign_groups: <GroupIcon sx={{ fontSize: 24, color: "#705D4A" }} />,
  set_manager: <SupervisorAccountIcon sx={{ fontSize: 24, color: "#5C6BC0" }} />,
  add_to_team: <GroupsIcon sx={{ fontSize: 24, color: "#5558AF" }} />,
  assign_sharepoint_site: <FolderSharedIcon sx={{ fontSize: 24, color: "#0078D4" }} />,
  send_welcome_email: <MailOutlineIcon sx={{ fontSize: 24, color: "#0078D4" }} />,
  notify_technician: <NotificationsActiveIcon sx={{ fontSize: 24, color: "#5558AF" }} />,
  send_email_from_msp: <ForwardToInboxIcon sx={{ fontSize: 24, color: "#0078D4" }} />,
  itglue_add_password: <LockIcon sx={{ fontSize: 24, color: "#5D4037" }} />,
  offboard_block_sign_in: <BlockIcon sx={{ fontSize: 24, color: "#C62828" }} />,
  offboard_revoke_sessions: <LogoutIcon sx={{ fontSize: 24, color: "#EF6C00" }} />,
  offboard_reset_password: <VpnKeyIcon sx={{ fontSize: 24, color: "#FF9800" }} />,
  offboard_list_auth_methods: <SecurityIcon sx={{ fontSize: 24, color: "#5C6BC0" }} />,
  offboard_remove_mfa: <SecurityIcon sx={{ fontSize: 24, color: "#7E57C2" }} />,
  offboard_set_out_of_office: <MailOutlineIcon sx={{ fontSize: 24, color: "#0078D4" }} />,
  offboard_set_forwarding: <ForwardToInboxIcon sx={{ fontSize: 24, color: "#0078D4" }} />,
  offboard_grant_onedrive_access: <CloudUploadIcon sx={{ fontSize: 24, color: "#0078D4" }} />,
  offboard_transfer_team_ownership: <SwapHorizIcon sx={{ fontSize: 24, color: "#5558AF" }} />,
  offboard_list_group_memberships: <ListAltIcon sx={{ fontSize: 24, color: "#705D4A" }} />,
  offboard_remove_from_groups: <GroupRemoveIcon sx={{ fontSize: 24, color: "#705D4A" }} />,
  offboard_convert_shared_mailbox: <MoveToInboxIcon sx={{ fontSize: 24, color: "#0078D4" }} />,
  offboard_remove_licenses: <MoneyOffCsredIcon sx={{ fontSize: 24, color: "#D84315" }} />,
  offboard_itglue_documentation: <DescriptionIcon sx={{ fontSize: 24, color: "#5D4037" }} />,
  offboard_delete_user: <DeleteForeverIcon sx={{ fontSize: 24, color: "#B71C1C" }} />,
};

export type FlowStep = {
  id: string;
  action: string;
  label: string;
  enabled: boolean;
  isCustom?: boolean;
  options?: Record<string, string>;
};

type CustomFlowStep = { id: string; action: string; isCustom: true; options?: Record<string, string> };

interface CustomWorkflowFlowDiagramProps {
  templateSteps: FlowStep[];
  customSteps: CustomFlowStep[];
  onStepClick: (stepIndex: number, step: FlowStep | CustomFlowStep) => void;
  onAddStep: () => void;
  onAddTechnicianChecklist: () => void;
  onDeleteStep?: (stepIndex: number, step: FlowStep | CustomFlowStep) => void;
  schedulerEnabled?: boolean;
}

const CustomWorkflowFlowDiagram: React.FC<CustomWorkflowFlowDiagramProps> = ({
  templateSteps,
  customSteps,
  onStepClick,
  onAddStep,
  onAddTechnicianChecklist,
  onDeleteStep,
  schedulerEnabled = false,
}) => {
  // Show every template step in definition order (including disabled), then custom steps.
  // Disabled steps appear muted; click enables them and opens configuration.
  const allSteps: Array<FlowStep | CustomFlowStep> = [
    ...templateSteps,
    ...customSteps,
  ];

  const FlowDownArrow = ({ sx }: { sx?: any }) => (
    <Box
      sx={{
        width: 2,
        height: 28,
        bgcolor: "#705D4A",
        borderRadius: 1,
        position: "relative",
        ...sx,
        "&::after": {
          content: '""',
          position: "absolute",
          left: "50%",
          bottom: -4,
          transform: "translateX(-50%)",
          width: 0,
          height: 0,
          borderLeft: "6px solid transparent",
          borderRight: "6px solid transparent",
          borderTop: "6px solid #705D4A",
        },
      }}
    />
  );

  const getDelayLine = (opts: Record<string, string>) => {
    if (!schedulerEnabled) return null;
    if (opts.schedule_delay_enabled !== "true") return null;
    const amount = (opts.schedule_delay_amount || "").trim();
    if (!amount) return null;
    const unit = (opts.schedule_delay_unit || "hours").trim();
    return `Delay: ${amount} ${amount === "1" && unit.endsWith("s") ? unit.slice(0, -1) : unit}`;
  };

  const withDelayLine = (opts: Record<string, string>, lines: string[] | null) => {
    const delayLine = getDelayLine(opts);
    if (!delayLine) return lines;
    return [delayLine, ...(lines || [])];
  };

  const getStepSubText = (step: FlowStep | CustomFlowStep) => {
    if (!("options" in step)) return null;
    const opts = step.options || {};
    const techInstruction = (opts.technician_instruction || "").trim();

    if (step.id === "assign_groups") {
      const defaults = (opts.default_group_names || "").trim();
      const lines: string[] = [];
      lines.push(`Default groups: ${defaults || "None"}`);
      if (techInstruction) lines.push(`Instruction for Theo AI: ${techInstruction}`);
      return withDelayLine(opts, lines);
    }

    if (step.id === "assign_sharepoint_site") {
      const rawSites = (opts.default_sites || "").trim();
      const role = (opts.default_role || "read").trim();
      const lines: string[] = [];
      lines.push(`Default sites: ${rawSites || "None"} (role: ${role || "read"})`);
      if (techInstruction) lines.push(`Instruction for Theo AI: ${techInstruction}`);
      return withDelayLine(opts, lines);
    }

    if (step.id === "assign_license") {
      const rawDefaultLicense = (opts.default_license || "").trim();
      return withDelayLine(opts, [`Default license: ${rawDefaultLicense || "None"}`]);
    }

    if (step.id === "set_email_signature") {
      const html = (opts.signature_html_template || "").trim();
      const lines: string[] = [];
      lines.push(html ? "Signature HTML template configured" : "Signature HTML from ticket / ask once");
      if (techInstruction) lines.push(`Instruction for Theo AI: ${techInstruction}`);
      return withDelayLine(opts, lines);
    }

    if (step.id === "set_manager") {
      const hint = (opts.default_manager_hint || "").trim();
      const lines: string[] = [];
      lines.push(hint ? `Manager hint: ${hint.length > 56 ? `${hint.slice(0, 56)}…` : hint}` : "Manager from ticket");
      if (techInstruction) lines.push(`Instruction for Theo AI: ${techInstruction}`);
      return withDelayLine(opts, lines);
    }

    if (step.id === "add_to_team") {
      const hint = (opts.default_team_hint || "").trim();
      const lines: string[] = [];
      lines.push(hint ? `Team: ${hint.length > 56 ? `${hint.slice(0, 56)}…` : hint}` : "Team from ticket / search");
      if (techInstruction) lines.push(`Instruction for Theo AI: ${techInstruction}`);
      return withDelayLine(opts, lines);
    }

    if (step.id === "create_user") {
      const customPw = opts.use_custom_password === "true";
      const lines: string[] = [];
      lines.push(customPw ? "Custom initial password" : "Auto-generated initial password");
      if (opts.force_change_next_sign_in === "false") lines.push("No forced change on first login");
      // Missing option = on: the workflow default is to run the duplicate check.
      lines.push(
        opts.check_recent_duplicates === "false"
          ? "Recent-duplicate check off"
          : "Checks for duplicate users created in the past 7 days"
      );
      // Opt-in: requester verification is on only when explicitly enabled.
      if (opts.verify_authorized_requester === "true") {
        const entries = (opts.authorized_requesters || "")
          .split(/[,;\n]/)
          .map((x) => x.trim())
          .filter(Boolean);
        lines.push(
          entries.length > 0
            ? `Verifies requester (${entries.length} authorized ${entries.length === 1 ? "entry" : "entries"})`
            : "Verifies requester — no authorized list yet (pauses for review)"
        );
      } else {
        lines.push("Requester verification off");
      }
      return withDelayLine(opts, lines);
    }

    if (step.id === "send_welcome_email") {
      const subj = (opts.welcome_subject_template || "").trim();
      const recip = (opts.welcome_recipient || "new_user").trim();
      const cc = (opts.welcome_cc_recipients || "").trim();
      const lines: string[] = [];
      lines.push(`To: ${recip === "ticket_contact" ? "Ticket contact" : "New user"}`);
      if (cc) lines.push(`CC: ${cc.length > 56 ? `${cc.slice(0, 56)}…` : cc}`);
      lines.push(`Subject: ${subj ? (subj.length > 56 ? `${subj.slice(0, 56)}…` : subj) : "Not set"}`);
      const pdfTemplate = (opts.welcome_pdf_template_filename || "").trim();
      if ((opts.welcome_pdf_template_object || "").trim()) {
        lines.push(`Welcome letter PDF: ${pdfTemplate || "template attached"}`);
      }
      return withDelayLine(opts, lines);
    }

    if (step.id === "itglue_add_password") {
      const nameTpl = (opts.itglue_password_name_template || "").trim();
      const orgHint = (opts.itglue_organization_hint || "").trim();
      const lines: string[] = [];
      lines.push(nameTpl ? (nameTpl.length > 64 ? `${nameTpl.slice(0, 64)}…` : nameTpl) : "Name template not set");
      if (orgHint) lines.push(`Org hint: ${orgHint.length > 48 ? `${orgHint.slice(0, 48)}…` : orgHint}`);
      return withDelayLine(opts, lines);
    }

    return withDelayLine(opts, null);
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        py: 2,
        fontFamily: "var(--theo-font-sans)",
      }}
    >
      {/* Start node */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          px: 2,
          py: 1.5,
          borderRadius: 2,
          bgcolor: "#E8F5E9",
          border: "2px solid #4CAF50",
          mb: 1,
        }}
      >
        <CheckCircleIcon sx={{ color: "#4CAF50", fontSize: 28 }} />
        <Typography sx={{ fontWeight: 600, color: "#2E7D32", fontSize: 14 }}>
          Workflow Start
        </Typography>
      </Box>

      {/* Arrow */}
      <FlowDownArrow sx={{ mb: 0.5 }} />

      {templateSteps.some((s) => !s.enabled) && (
        <Typography
          variant="caption"
          sx={{
            display: "block",
            textAlign: "center",
            color: "#888",
            fontFamily: "var(--theo-font-sans)",
            fontSize: "0.72rem",
            maxWidth: 320,
            mb: 1,
            px: 1,
          }}
        >
          Muted steps are off. Click to enable and configure (they are not run until enabled).
        </Typography>
      )}

      {allSteps.map((step, index) => {
        const stepNum = index + 1;
        const rawLabel = "label" in step ? step.label : (step.action || "Add action name").replace(/_/g, " ");
        const label = stripLeadingWorkflowStepNumber(rawLabel);
        const stepId = step.id;
        const icon = STEP_ICONS[stepId] || <AddCircleOutlineIcon sx={{ fontSize: 24, color: "#666" }} />;
        const isCustom = "isCustom" in step && step.isCustom;
        const isTemplateOff = !isCustom && "enabled" in step && step.enabled === false;
        const subTextLines = getStepSubText(step);

        return (
          <React.Fragment key={step.id}>
            <Box
              onClick={() => onStepClick(index, step)}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                px: 2,
                py: 1.5,
                minWidth: 220,
                borderRadius: 2,
                border: isTemplateOff ? "2px dashed #C4B8AE" : "2px solid #705D4A",
                bgcolor: isTemplateOff ? "#F3F2F0" : "#FFFBF8",
                opacity: isTemplateOff ? 0.92 : 1,
                cursor: "pointer",
                transition: "all 0.2s",
                "&:hover": {
                  bgcolor: isTemplateOff ? "#ECEAE7" : "#F5F0EB",
                  boxShadow: "0 2px 8px rgba(112,93,74,0.2)",
                },
              }}
            >
              <Box sx={{ flexShrink: 0 }}>{icon}</Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                  <Typography
                    sx={{
                      fontWeight: 600,
                      color: isTemplateOff ? "#757575" : "#333",
                      fontSize: 14,
                    }}
                  >
                    {stepNum}. {label}
                  </Typography>
                  {isTemplateOff && (
                    <Chip label="Off" size="small" sx={{ height: 22, fontSize: "0.65rem", bgcolor: "#E0E0E0" }} />
                  )}
                </Box>
                {isCustom && (
                  <Typography variant="caption" sx={{ color: "#888", fontSize: 11 }}>
                    Custom step
                  </Typography>
                )}

              {subTextLines && (
                <Box sx={{ mt: 0.75, width: "100%" }}>
                  {subTextLines.slice(0, 2).map((line, i) => (
                    <Typography
                      // eslint-disable-next-line react/no-array-index-key
                      key={i}
                      variant="caption"
                      sx={{
                        display: "block",
                        color: "#888",
                        fontFamily: "var(--theo-font-sans)",
                        fontSize: "0.72rem",
                        whiteSpace: "pre-wrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                      title={line}
                    >
                      {line}
                    </Typography>
                  ))}
                </Box>
              )}
              </Box>
              {onDeleteStep && (
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    onDeleteStep(index, step);
                  }}
                  sx={{ color: "#999", "&:hover": { color: "#C62828" } }}
                  aria-label="Delete step"
                >
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              )}
            </Box>
            {index < allSteps.length - 1 && (
              <FlowDownArrow sx={{ my: 0.5 }} />
            )}
          </React.Fragment>
        );
      })}

      {/* Add step button */}
      <FlowDownArrow sx={{ my: 0.5 }} />
      <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", justifyContent: "center" }}>
        <Box
          onClick={onAddStep}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            px: 2,
            py: 1.5,
            borderRadius: 2,
            border: "2px dashed #BDBDBD",
            bgcolor: "#FAFAFA",
            cursor: "pointer",
            color: "#705D4A",
            "&:hover": { borderColor: "#705D4A", bgcolor: "#FFFBF8" },
          }}
        >
          <AddCircleOutlineIcon sx={{ fontSize: 24 }} />
          <Typography sx={{ fontWeight: 500, fontSize: 14 }}>Add Theo step</Typography>
        </Box>
        <Box
          onClick={onAddTechnicianChecklist}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            px: 2,
            py: 1.5,
            borderRadius: 2,
            border: "2px dashed #BDBDBD",
            bgcolor: "#FAFAFA",
            cursor: "pointer",
            color: "#705D4A",
            "&:hover": { borderColor: "#705D4A", bgcolor: "#FFFBF8" },
          }}
        >
          <AddCircleOutlineIcon sx={{ fontSize: 24 }} />
          <Typography sx={{ fontWeight: 500, fontSize: 14 }}>Add Technician checklist</Typography>
        </Box>
      </Box>

      {/* End node */}
      <FlowDownArrow sx={{ my: 0.5 }} />
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          px: 2,
          py: 1.5,
          borderRadius: 2,
          bgcolor: "#FFEBEE",
          border: "2px solid #F44336",
          mt: 1,
        }}
      >
        <CheckCircleIcon sx={{ color: "#F44336", fontSize: 28 }} />
        <Typography sx={{ fontWeight: 600, color: "#C62828", fontSize: 14 }}>
          Workflow End
        </Typography>
      </Box>
    </Box>
  );
};

export default CustomWorkflowFlowDiagram;
